import { Badge, Popover } from '@mui/material';
import { styled } from '@mui/system';
import Icon from 'Components/_NewElements/Icon';
import ActionButtonRounded from 'Components/ActionButtonRounded';
import ToolTip from 'Components/ToolTip';
import { contraIndicationsInquiry as ninmtContraIndicationsInquiry } from 'neuro-schemas/contraIndicationsInquiry';
import { nowPartialDate, partialDateFromDate } from 'neuro-utils';
import * as React from 'react';
import { useIntl } from 'react-intl';
import { actions } from 'Store/documents/actions';
import { useAppDispatch, useAppSelector } from 'Store/index';
import browserLanguage from 'Utility/browserLanguage';
import { formatPartialDate } from 'Utility/date';
import { filterDocumentsByType, omitControlProps, sortDocumentsDescending } from 'Utility/documentHandling';
import colors from '../../../config/theme/colors';
import AlertsSnack, { RenderAlerts } from '../../Alerts';
import { Container, Item } from '../../Grid';
import { badgeSX } from './Shared';

import type { TooltipProps } from '@mui/material';
import type { TRtmsContraIndicationOption } from 'neuro-schemas/contraIndicationsInquiry';
import type { IntlShape } from 'react-intl';

type TFormatMessageValues = Parameters<IntlShape['formatMessage']>[1];
type TFormatMessageReturn = ReturnType<IntlShape['formatMessage']>;

const PopoverArea = styled('div')({
  padding: '2rem',
  minHeight: '0rem',
  maxHeight: '60rem',
  minWidth: '50rem',
});

const PopoverHeader = styled('div')({
  fontSize: '2.2rem',
  marginBottom: '2rem',
});

const NoAlertsText = styled('div')({
  fontSize: '2rem',
  color: colors.gray,
});

const highestSeverity = (alerts: IAlert[]): 'error' | 'primary' | 'warning' =>
  alerts.map(a => a.severity).includes('error')
    ? 'error'
    : alerts.map(a => a.severity).includes('warning')
      ? 'warning'
      : 'primary';

const getContraIndicationsNotification = (
  alert: IAlert,
  contraIndicationsInquiry: IDocumentWithTypeV2<'contraIndicationsInquiry'>,
  uiLanguage: Locale,
  loading: boolean,
  handleClick: () => void,
  fm: (id: string, values?: TFormatMessageValues) => TFormatMessageReturn,
) => {
  const notifTitle =
    contraIndicationsInquiry.medicalOperations === 'no'
      ? fm('alerts.ninmt.contraIndicationsInquiryNo', {
          strong: chunks => (
            <span key={String(chunks)} style={{ fontWeight: '600' }}>
              {React.Children.toArray(chunks)}
            </span>
          ),
          date: formatPartialDate(partialDateFromDate(new Date(contraIndicationsInquiry._docCreateDate))),
        })
      : fm('alerts.ninmt.contraIndicationsInquiryYes', {
          strong: chunks => (
            <span key={String(chunks)} style={{ fontWeight: 'bold' }}>
              {React.Children.toArray(chunks)}
            </span>
          ),
        });
  const rtmsAbsolutes = new Set<TRtmsContraIndicationOption>([
    'foreignObjectsInBrain',
    'otherMagneticForeignObjectsInHead',
    'pacemaker',
    'metallicCardiacValveProsthesis',
    'innerEarProsthesis',
    'stimulatorAtNeck',
  ]);

  const rtmsRelatives = new Set<TRtmsContraIndicationOption>([
    'intracranialForeignObjects',
    'insulinPump',
    'infusionPortsAtUpperBody',
    'nerveStimulators',
    'epilepsy',
    'diseasesWithLowerSeizureThreshold',
    'pregnancy',
    'nonRemovableTattoosOrPiercingsInHead',
  ]);

  const contraIndicationsDescriptionItems: {
    rtmsAbsolute?: string[];
    rtmsRelative?: string[];
    tdcsAbsolute?: string[];
    details?: string;
  } =
    contraIndicationsInquiry.rtmsContraIndications &&
    contraIndicationsInquiry.rtmsContraIndications.length > 0 &&
    !contraIndicationsInquiry.rtmsContraIndications.includes('noneOfThese')
      ? {
          rtmsAbsolute: contraIndicationsInquiry.rtmsContraIndications.filter(ci => rtmsAbsolutes.has(ci)),
          rtmsRelative: contraIndicationsInquiry.rtmsContraIndications.filter(ci => rtmsRelatives.has(ci)),
        }
      : {};

  if (
    contraIndicationsInquiry.tdcsContraIndications &&
    contraIndicationsInquiry.tdcsContraIndications.length > 0 &&
    !contraIndicationsInquiry.tdcsContraIndications.includes('noneOfThese')
  ) {
    contraIndicationsDescriptionItems['tdcsAbsolute'] = contraIndicationsInquiry.tdcsContraIndications;
  }
  if (
    contraIndicationsInquiry.rtmsContraIndications?.includes('noneOfThese') ||
    contraIndicationsInquiry.tdcsContraIndications?.includes('noneOfThese')
  ) {
    contraIndicationsDescriptionItems['details'] = contraIndicationsInquiry.healthChangeDetails ?? undefined;
  }

  const locales = ninmtContraIndicationsInquiry.localizations[uiLanguage];

  const contraIndicationsDesc = (): React.JSX.Element => (
    <Container direction={'column'} style={{ marginTop: '1rem' }}>
      {contraIndicationsDescriptionItems.rtmsAbsolute && contraIndicationsDescriptionItems.rtmsAbsolute.length > 0 && (
        <>
          <Item style={{ fontWeight: '600' }}>
            {fm('diagnosis.ninmt.contraIndicationsToTreatment.tmsAbsoluteContraIndications')}
          </Item>
          {contraIndicationsDescriptionItems.rtmsAbsolute.map(a => {
            return <Item key={a}>{`- ${locales[`opts.${a}`]}`}</Item>;
          })}
        </>
      )}
      {contraIndicationsDescriptionItems.rtmsRelative && contraIndicationsDescriptionItems.rtmsRelative.length > 0 && (
        <>
          <Item style={{ fontWeight: '600' }}>
            {fm('diagnosis.ninmt.contraIndicationsToTreatment.tmsRelativeContraIndications')}
          </Item>
          {contraIndicationsDescriptionItems.rtmsRelative.map(a => {
            return <Item key={a}>{`- ${locales[`opts.${a}`]}`}</Item>;
          })}
        </>
      )}
      {contraIndicationsDescriptionItems.tdcsAbsolute && contraIndicationsDescriptionItems.tdcsAbsolute.length > 0 && (
        <>
          <Item style={{ fontWeight: '600' }}>
            {fm('diagnosis.ninmt.contraIndicationsToTreatment.dcsAbsoluteContraIndications')}
          </Item>
          {contraIndicationsDescriptionItems.tdcsAbsolute.map(a => {
            return <Item key={a}>{`- ${locales[`opts.${a}`]}`}</Item>;
          })}
        </>
      )}
      {contraIndicationsDescriptionItems.details && (
        <>
          <Item style={{ fontWeight: '600' }}>{fm('alerts.ninmt.healthChangeDetails')}</Item>
          <Item>{contraIndicationsDescriptionItems.details}</Item>
        </>
      )}
      {contraIndicationsInquiry.medicalOperations === 'no' && (
        <div style={{ alignSelf: 'flex-end' }}>
          <ActionButtonRounded
            text="alerts.ninmt.contraIndicationButton"
            onClick={handleClick}
            loading={loading}
            colorScheme={'warning'}
            uppercase={false}
            fontSize={16}
            height={4}
            width={32}
          />
        </div>
      )}
    </Container>
  );
  return {
    textID: 'alerts.ninmt.contraIndicationsInquiry',
    severity: alert.severity,
    customText: notifTitle,
    desc: contraIndicationsDesc(),
    id: alert.id,
  };
};

const SyncWarningTemplate = ({ ...props }) => {
  const { title, secondaryTitle, failedSyncs } = { title: '', secondaryTitle: '', failedSyncs: undefined, ...props };
  return (
    <>
      <div style={{ fontWeight: 600 }}>{title}</div>
      <div>{secondaryTitle}</div>
      <ul>
        {Array.isArray(failedSyncs) &&
          (failedSyncs as string[]).map(s => (
            <li key={s} style={{ fontWeight: 600 }}>
              {s}
            </li>
          ))}
      </ul>
    </>
  );
};

export const AlertsPopUps = ({ alerts }: { alerts: IAlert[] }): React.JSX.Element => {
  const uiLanguage = useAppSelector(s => s.settings.userSettings.uiLanguage) ?? browserLanguage;
  const [buttonLoading, setButtonLoading] = React.useState(false);
  const dispatch = useAppDispatch();
  const { formatMessage } = useIntl();
  const fm = (id: string, values?: TFormatMessageValues) => formatMessage({ id }, values);

  const allDocs = useAppSelector(s => s.documents.sortedAndMergedDocuments) ?? [];
  const myAppDocs = useAppSelector(s => s.myapp.sortedAndMergedDocuments);

  const extraSnacks = useAppSelector(s => s.session.snacks?.extraSnacks) ?? [];
  const hiddenSnacks = useAppSelector(s => s.session.snacks?.hiddenSnacks) ?? [];
  const filteredSnacks = [...alerts.filter(a => !hiddenSnacks.includes(a.id)), ...extraSnacks];

  return (
    <AlertsSnack
      alerts={filteredSnacks
        .filter(a => a.popup)
        .map(b => {
          if (b.content === 'alerts.ninmt.contraIndicationsInquiry') {
            const latestContraIndInquiry = filterDocumentsByType(myAppDocs, ['contraIndicationsInquiry']).sort(
              sortDocumentsDescending,
            )[0];

            const latestContraIndicDoc = filterDocumentsByType(allDocs, ['contraIndicationsToTreatment']).sort(
              sortDocumentsDescending,
            )[0];

            const handleClick = () => {
              setButtonLoading(true);

              if (latestContraIndicDoc) {
                void actions.putDocument('contraIndicationsToTreatment', {
                  ...omitControlProps(latestContraIndicDoc),
                  date: nowPartialDate(),
                })(dispatch);
              }
              setButtonLoading(false);
            };

            if (latestContraIndInquiry) {
              return getContraIndicationsNotification(
                b,
                latestContraIndInquiry,
                uiLanguage,
                buttonLoading,
                handleClick,
                fm,
              );
            }
          }
          if (typeof b.content !== 'string') {
            // Supply the snack with an element as content. Elements cant be serialized in redux so we need a template for them.
            const TemplateComponent = b.content.templateName === 'syncwarning' ? SyncWarningTemplate : undefined;
            const Element = TemplateComponent && React.createElement(TemplateComponent, { ...b.content });
            return { severity: b.severity, customText: Element, textID: 'customtextAlert', id: b.id };
          } else return { textID: b.content, severity: b.severity, id: b.id, fillNewButton: b.fillNewButton };
        })}
    />
  );
};

export const AlertsPopover = ({
  iconSize,
  alerts,
  alternateStyle,
  tooltipPlacement,
}: {
  iconSize?: number;
  alerts: IAlert[];
  alternateStyle?: boolean;
  tooltipPlacement?: TooltipProps['placement'];
}): React.JSX.Element => {
  const [el, setEl] = React.useState<SVGSVGElement | HTMLElement | null>(null);
  const [buttonLoading, setButtonLoading] = React.useState(false);
  const { formatMessage } = useIntl();
  const fm = (id: string, values?: TFormatMessageValues) => formatMessage({ id }, values);
  const dispatch = useAppDispatch();

  const handleOpen = (event: React.MouseEvent<SVGSVGElement | HTMLElement>): void =>
    !el ? setEl(event.currentTarget) : setEl(null);

  const allDocs = useAppSelector((s: IState) => s.documents.sortedAndMergedDocuments) ?? [];
  const myAppDocs = useAppSelector((s: IState) => s.myapp.sortedAndMergedDocuments);
  const uiLanguage = useAppSelector((state: IState) => state.settings.userSettings.uiLanguage) ?? browserLanguage;

  return (
    <React.Fragment>
      <Popover
        open={!!el}
        onClose={handleOpen}
        anchorEl={el}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        elevation={1}
        disableScrollLock
      >
        <PopoverArea>
          <PopoverHeader>{fm('alerts.notifications')}</PopoverHeader>
          {alerts.length > 0 ? (
            <RenderAlerts
              alerts={alerts.map(b => {
                if (b.content === 'alerts.ninmt.contraIndicationsInquiry') {
                  const latestContraIndInquiry = filterDocumentsByType(myAppDocs, ['contraIndicationsInquiry'])
                    .sort(sortDocumentsDescending)
                    .at(0);

                  const latestContraIndicDoc = filterDocumentsByType(allDocs, ['contraIndicationsToTreatment'])
                    .sort(sortDocumentsDescending)
                    .at(0);

                  const handleClick = () => {
                    setButtonLoading(true);

                    if (latestContraIndicDoc) {
                      void actions.putDocument('contraIndicationsToTreatment', {
                        ...omitControlProps(latestContraIndicDoc),
                        date: nowPartialDate(),
                      })(dispatch);
                    }
                    setButtonLoading(false);
                  };

                  if (latestContraIndInquiry) {
                    return getContraIndicationsNotification(
                      b,
                      latestContraIndInquiry,
                      uiLanguage,
                      buttonLoading,
                      handleClick,
                      fm,
                    );
                  }
                }
                if (typeof b.content !== 'string') {
                  // Supply the snack with an element as content. Elements cant be serialized in redux so we need a template for them.
                  const TemplateComponent = b.content.templateName === 'syncwarning' ? SyncWarningTemplate : undefined;
                  const Element = TemplateComponent && React.createElement(TemplateComponent, { ...b.content });
                  return { severity: b.severity, customText: Element, textID: 'customtextAlert', id: b.id };
                } else return { textID: b.content, severity: b.severity, id: b.id, fillNewButton: b.fillNewButton };
              })}
              topMargin={false}
              closeable={false}
              elevation={0}
              // Alert is not actually closeable, but close the Popover with the Alert if handleClose is triggered externally
              handleClose={() => setEl(null)}
            />
          ) : (
            <Container alignItems="center" justifyContent="center" style={{ height: '5rem', marginBottom: '1rem' }}>
              <Item>
                <NoAlertsText>{fm('alerts.noNotifications')}</NoAlertsText>
              </Item>
            </Container>
          )}
        </PopoverArea>
      </Popover>
      <ToolTip
        title={fm('alerts.notifications')}
        placement={tooltipPlacement}
        hover
        content={
          <Badge
            badgeContent={alerts.length}
            color={highestSeverity(alerts)}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            sx={badgeSX(alternateStyle)}
          >
            <div onClick={handleOpen}>
              <Icon size={iconSize} icon="bell" color="appBlueLight" button />
            </div>
          </Badge>
        }
      />
    </React.Fragment>
  );
};
